/**
 * DropPage - Creator / Community page tree (Drop-In product)
 *
 * Renders a creator/community Drop based on configuration from /drops/slug/:slug.
 * Composition:
 * - Hero/Jumbotron with featured game
 * - Multi-streaming + chat (when live)
 * - Tab nav (CHALLENGES / CLIPS / SOCIAL / MERCH conditional)
 * - Dynamic content blocks
 *
 * URL: /drop-in/:slug (e.g., /drop-in/teepee)
 *
 * Peer of HubPage (brand surface at /hub/:slug). Both compose Widget Runtime
 * widgets from web/src/widgets/. Neither imports from the other.
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { useParams, useNavigate } from "react-router";
import getConfig from "@/utils/getConfig";
import { useSelector, useDispatch } from "react-redux";
import { EntityType, TrackSurface } from "@devour/client";
import { isPublicDrop } from "@/surfaces/resolveStyle";
import { useTrackImpression } from "@/hooks/useTrackImpression";
import { useTrackClick } from "@/hooks/useTrackClick";
import { toggleLoginOpen } from "@/redux/meta/metaActions";

import { IStore } from "@/redux/defaultStore";

// Hooks
import {
    useGetDrop,
    getEnabledDropSections,
    type DropSection,
} from "@/hooks/drop/useGetDrop";
import type { StreamingSettings } from "@/hooks/hub/useGetHub";
import { useGetUserProfile } from "@/hooks/useGetUserProfile";
import { useGetUserLevel } from "@/hooks/useGetUserLevel";
import { useHubPulse } from "@/hooks/hub/useHubPulse";

// Hooks used inside DropPage's data setup.
import { useGetDropDiscordWidget } from "@/hooks/drop/useGetDropDiscordWidget";
import { useCreateDropRealmSignin } from "@/hooks/drop/useCreateDropRealmSignin";
import useOnAuthThirdweb from "@/hooks/useOnAuthThirdweb";
import { useGetDropLiveStatus } from "@/hooks/drop/useGetDropLiveStatus";
import { useGetChallenge } from "@/hooks/challenges/useGetChallenge";

// Tenant type — only consumed as a prop forwarded to DropPageMain.
import type { TenantId } from "@/widgets/ThemeProvider";
import { useDropPageEntry } from "./sections/useDropPageEntry";
import { useDropPageArchetype } from "./sections/useDropPageArchetype";
import { buildLiveIdentitySlot } from "./sections/liveIdentitySlot";
import { dropPageMeta } from "./sections/dropPageMeta";
import { getLiveStreamStatus } from "./sections/liveStreamStatus";

// Section dispatch — DropPage builds the closure, DropPageMain invokes it.
import { renderSectionContent as renderDropSectionContent } from "./DropSectionRenderer";
// Loading + error states.
import { DropPageLoading, DropPageError } from "./DropPageStates";
// Main JSX body — everything from ThemeProvider down lives here.
import { DropPageMain } from "./DropPageMain";
import { RealmHandshakeCapture } from "@/components/RealmHandshakeCapture";

// Styles
import "./DropPage.scss";

/**
 * DropPage — Creator / Community page tree.
 *
 * Reads from the `drops` collection at /drops/slug/:slug. Renders the
 * full creator/community experience: identity header, hero, live streaming,
 * tab nav, content blocks, member leaderboard, referral, footer.
 *
 * Peer of HubPage (brand surface). Neither imports from the other —
 * both compose Widget Runtime widgets from web/src/widgets/.
 */
interface DropPageProps {
    slug?: string;
}

export const DropPage = ({ slug: slugProp }: DropPageProps = {}) => {
    const routeParams = useParams<{ slug?: string; vanitySlug?: string }>();
    const slug = slugProp || routeParams.slug || routeParams.vanitySlug;
    const navigate = useNavigate();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const isLoginOpen = useSelector((store: IStore) => store.metaStore.isLoginOpen);
    const dispatch = useDispatch();

    // Fetch Drop configuration
    const { data: drop, isLoading, error } = useGetDrop(slug);

    /* One-time URL param capture + OAuth continuations — see useDropPageEntry.ts. */
    const {referralUserId, postClaim, postClaimDismissed, setPostClaimDismissed, portalLoginUrl, tenantId, otherPlatform, addOtherPlatformUrl} =
        useDropPageEntry(drop?.socials);

    /*
     * Drop page visit — the canonical "visit" metric for a Drop. Tracked as
     * EntityType.drop / DropPage so drops report under their own bucket in the
     * platform + drop-in analytics dashboards (previously misattributed to Hub).
     */
    useTrackImpression(EntityType.drop, drop?.id, TrackSurface.drop_page);

    /*
     * Section-tab navigation click tracker (Streaming / Challenges / Clips /
     * Social / Merch). Wrapped onto the section setter below so user-driven
     * switches fire a DropInSection click, while the one-time auto-activate
     * effect uses the raw setter and is NOT counted as a nav.
     */
    const trackSectionNav = useTrackClick(EntityType.drop, drop?.id, TrackSurface.drop_in_section);

    // Fetch user profile for XP/Level display
    const { data: userProfile } = useGetUserProfile(fullToken);
    const user = userProfile?.user;
    /* The stored `user.level` only moves on an XP event; the derived level is current. */
    const { data: accountLevel } = useGetUserLevel(fullToken, user?.id);

    /*
     * Pulse intentionally disabled on Drop pages — `useHubPulse` hits the
     * Hub-scoped `/pulse/hub/:slug` endpoint, which doesn't know about Drops
     * and would (a) 404 every 60s for Drops that don't have a matching Hub
     * slug, or (b) return a stale Hub follower count when a same-slug Hub
     * does exist in `brandhubs`. The fallback `drop.followerCount` already
     * carries the canonical count; PulseBar gracefully handles undefined.
     * Drop-native pulse is a follow-up.
     *
     * Stub the consumer interface directly (don't call the hook with
     * `undefined`) so the safety property doesn't depend on the hook's
     * internal enabled-guard.
     */
    const pulse = undefined;
    const pulseLoading = false;

    /*
     * Drops do not currently expose custom-points or hub-scoped season passes.
     * Both are Hub-only features (configured at the HubModel level). The Drop
     * surface uses default DevourPlay XP and no season-pass tier bar. Adding
     * those would be a separate scoped feature, not part of the page-tree
     * decouple.
     */

    // State
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [activePageIndex, setActivePageIndex] = useState(0);

    // Derived data - filter sections for mode cards vs header icons
    const allEnabledSections = useMemo(() => drop ? getEnabledDropSections(drop) : [], [drop]);

    /*
     * For CREATOR drops, auto-activate the STREAMING section ONCE on first
     * load so the creator section module renders as the default landing
     * view. A ref guard ensures we apply this default exactly once per
     * mount, so when `allEnabledSections` re-derives on a query refetch
     * we don't hijack the user back to STREAMING after they navigated
     * elsewhere.
     */
    const dropType = drop?.dropType;
    const initialSectionAppliedRef = useRef(false);
    useEffect(() => {
        if (initialSectionAppliedRef.current) return;
        if (dropType !== "CREATOR") return;
        const streamingSec = allEnabledSections.find(s => s.type === "STREAMING");
        if (streamingSec?._id) {
            setActiveSection(streamingSec._id);
            initialSectionAppliedRef.current = true;
        }
    }, [dropType, allEnabledSections]);

    /*
     * T15 — the Realm door. Inside a signed Realm embed (handshake in
     * rttrStore) with no session, and the Drop running an onboarding
     * experience, sign in codelessly through the Drop's own door. One
     * attempt per mount; ANY failure falls through silently to the standard
     * email + code login — the user shouldn't know codeless was attempted.
     */
    const dropRealmSignin = useCreateDropRealmSignin();
    const realmUuid = useSelector((store: IStore) => store.rttrStore?.realmUuid);
    const { onReauthorize } = useOnAuthThirdweb();
    const realmSigninAttemptedRef = useRef(false);
    useEffect(() => {
        if (realmSigninAttemptedRef.current) return;
        if (fullToken || !slug || !realmUuid) return;
        if (!drop?.onboardingExperienceId) return;
        realmSigninAttemptedRef.current = true;
        dropRealmSignin.mutateAsync({ slug })
            .then(token => onReauthorize(token, true))
            .catch(() => {
                /* Fall through to the standard login. */
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullToken, slug, realmUuid, drop?.onboardingExperienceId]);

    /*
     * All enabled sections become mode cards
     * Note: No default sections - only show what's explicitly configured in admin
     */
    const enabledSections = allEnabledSections;
    // Optional header quick-access icons (sections still appear as mode cards too)
    const streamingSection = allEnabledSections.find(s => s.type === "STREAMING");
    const infoSection = allEnabledSections.find(s => s.type === "INFO");
    const competitionId = drop?.competitionId;

    // Live stream status derived from hub streaming settings (no extra fetch needed)
    /*
     * Persistent fallback (only reliable when EventSub webhook has flipped
     * the field) — kept as a default so isCompact/LIVE pulse work in offline
     * preview contexts where /live-status isn't called.
     */
    const persistentLiveStatus = useMemo(() => getLiveStreamStatus(drop?.streamingSettings as unknown as StreamingSettings | undefined), [drop?.streamingSettings]);

    /*
     * Fresh truth from /live-status (30s cache, 60s refetch). React Query
     * dedupes with the StreamPanel call so this is a single network hit per
     * drop. When fresh data is available it overrides the persistent field —
     * required because persistent field never updates on initial-creation-
     * mid-stream (EventSub fires on TRANSITIONS only).
     */
    const liveStatusQuery = useGetDropLiveStatus(slug);
    const freshAnyLive = (liveStatusQuery.data as { anyLive?: boolean } | null | undefined)?.anyLive;
    const hasLiveStreams = typeof freshAnyLive === "boolean" ? freshAnyLive : persistentLiveStatus.hasLive;

    /*
     * Discord widget online count powers the hero's "● N online" stat. Same
     * hook is also called inside SocialMosaic — React Query dedupes by key,
     * so this is one network round-trip total per drop.
     */
    const discordWidget = useGetDropDiscordWidget(slug);
    const discordOnlineCount = (discordWidget.data as { widget?: { presenceCount?: number } } | null | undefined)?.widget?.presenceCount;

    /* Multi-page archetype + per-page block filter — see useDropPageArchetype.ts. */
    const {pageLabels, isMultiPage, activePageBlocks} = useDropPageArchetype(drop, activePageIndex);

    // Get challenge ID from missions section for Explore More promotions
    const missionsSection = allEnabledSections.find(s => s.type === "MISSIONS");
    const missionsChallengeId = missionsSection?.challengeId;
    const { data: missionsChallenge } = useGetChallenge(missionsChallengeId);

    /*
     * Drops use the default DevourPlay XP system (no custom-points override).
     * If custom points become a Drop feature later, this is the seam to put
     * the per-drop pointSystemConfig branch back into.
     */
    const pointDisplay = useMemo(() => ({
        pointsName: "DevourPlay XP",
        pointsIconUrl: undefined as string | undefined,
        currentPoints: user?.experiencePoints || 0,
        currentLevel: accountLevel?.level || user?.level || 1,
        nextLevelPoints: 1000,
    }), [user?.experiencePoints, user?.level, accountLevel?.level]);

    /*
     * NOTE: We intentionally do NOT set an initial active section
     * User clicks a mode card to reveal content (like the /play dashboard)
     */

    // Cleanup body overflow on unmount
    useEffect(() => {
        return () => {
            document.body.style.overflow = "";
            document.body.style.paddingRight = "0";
        };
    }, []);

    /*
     * Resolve hero background URL using same fallback chain as Hero (@/widgets/Hero).
     * Fallback to the rendered banner <img> src if API fields are empty
     * (creator hubs may populate the banner via social profile sync).
     */
    const resolvedHeroImageUrl = drop?.heroSection?.heroImageUrl ||
        drop?.heroSection?.heroImageExternalUrl ||
        drop?.creatorHeaderImageUrl ||
        drop?.socials?.find(s => s.bannerImageUrl)?.bannerImageUrl;

    const handleLoginRequired = () => dispatch(toggleLoginOpen(true));
    const handleInviteClick = () => {
        if (!fullToken) {
            dispatch(toggleLoginOpen(true));
        } else {
            setIsInviteModalOpen(true);
        }
    };

    // Get the currently active section object
    const currentSection = enabledSections.find(s => s._id === activeSection);

    /*
     * Tracked section setter passed to DropPageMain in place of the raw
     * setState. Fires a DropInSection nav click only when opening a concrete
     * section (a string id) — closing/deselecting (null) and functional updaters
     * are not counted as navigations, so the metric reflects section opens.
     */
    const handleSectionNav: typeof setActiveSection = (value) => {
        if (typeof value === "string") trackSectionNav();
        setActiveSection(value);
    };

    /*
     * Live layout for CREATOR drops: when the creator is live, the stream is
     * promoted to the top of the section (CreatorSection) and the full hero
     * above is suppressed (DropPageMain). The creator identity relocates beneath
     * the stream as a compact DropHero in identityOnly mode — built here so all
     * follow/login/invite handlers stay wired. undefined when offline.
     */
    const liveLayout = hasLiveStreams && drop?.dropType === "CREATOR";
    const liveIdentitySlot = liveLayout && drop
        ? buildLiveIdentitySlot({drop, onInviteClick: handleInviteClick, onLoginRequired: handleLoginRequired, referralUserId})
        : undefined;

    /*
     * Section dispatch lives in DropSectionRenderer to keep this file under
     * 400 lines. Adding a new section type means editing the switch there,
     * not scrolling past 100 lines of unrelated state setup here.
     */
    const renderSectionContent = (section: DropSection) =>
        renderDropSectionContent(section, {
            drop: drop as unknown as Parameters<typeof renderDropSectionContent>[1]["drop"],
            slug,
            competitionId,
            liveLayout,
            identitySlot: liveIdentitySlot,
            onLoginRequired: handleLoginRequired,
            referralUserId,
        });

    // Loading + error states moved to DropPageStates.tsx
    if (isLoading) return <DropPageLoading />;
    if (error || !drop) return <DropPageError />;

    /*
     * `isPublicDrop`, not `drop.visibility` alone — this used to read only the
     * legacy field while `DropPageRouter.tsx` read it distribution-aware, so
     * the two could disagree about which channel a page load was for. See
     * `isPublicDrop`'s doc comment.
     */
    const isPublicHub = isPublicDrop(drop);

    /* Page meta — precedence mirrors the edge worker; see dropPageMeta.ts. */
    const {metaTitle, metaDescription, metaImage, canonicalUrl} = dropPageMeta(drop, slug);

    return (
        <>
            {/* An embed pointed STRAIGHT at /drop-in/:slug must land its
                handshake too — only the hub cutover route captured it, so the
                direct route's codeless door never fired. */}
            <RealmHandshakeCapture />
            <Helmet>
                <title>{metaTitle}</title>
                <meta name="description" content={metaDescription} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:image" content={metaImage} />
                <meta property="og:site_name" content="DevourPlay" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@devourplay" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={metaImage} />
            </Helmet>
            {postClaim && !postClaimDismissed &&
                <div className="drop-post-claim" role="status">
                    <div className="drop-post-claim__body">
                        <strong className="drop-post-claim__title">
                            {postClaim.created ? "Your Drop is live!" : "Drop claimed — it's yours."}
                        </strong>
                        <span className="drop-post-claim__sub">
                            This is your public Drop page. Customize it, connect your socials, and track your community from the Creator Portal.
                        </span>
                    </div>
                    <div className="drop-post-claim__actions">
                        {portalLoginUrl &&
                            <a className="drop-post-claim__cta" href={portalLoginUrl}>
                                Manage my Drop →
                            </a>
                        }
                        {addOtherPlatformUrl &&
                            <a className="drop-post-claim__cta drop-post-claim__cta--secondary" href={addOtherPlatformUrl}>
                                Add your {otherPlatform === "twitch" ? "Twitch" : "Kick"} →
                            </a>
                        }
                        <button
                            type="button"
                            className="drop-post-claim__dismiss"
                            aria-label="Dismiss"
                            onClick={() => setPostClaimDismissed(true)}
                        >
                            ✕
                        </button>
                    </div>
                </div>
            }
            <DropPageMain
                drop={drop}
                slug={slug}
                tenantId={tenantId}
                isPublicHub={isPublicHub}
                isLoginOpen={isLoginOpen}
                dispatch={dispatch}
                enabledSections={enabledSections}
                streamingSection={streamingSection}
                infoSection={infoSection}
                activeSection={activeSection}
                setActiveSection={handleSectionNav}
                currentSection={currentSection}
                renderSectionContent={renderSectionContent}
                hasLiveStreams={hasLiveStreams}
                pulse={pulse}
                pulseLoading={pulseLoading}
                discordOnlineCount={discordOnlineCount}
                user={user}
                handleLoginRequired={handleLoginRequired}
                handleInviteClick={handleInviteClick}
                referralUserId={referralUserId}
                resolvedHeroImageUrl={resolvedHeroImageUrl}
                pointDisplay={pointDisplay}
                activePageBlocks={activePageBlocks}
                isMultiPage={isMultiPage}
                pageLabels={pageLabels}
                activePageIndex={activePageIndex}
                setActivePageIndex={setActivePageIndex}
                isInviteModalOpen={isInviteModalOpen}
                setIsInviteModalOpen={setIsInviteModalOpen}
                missionsChallenge={missionsChallenge}
                toggleLoginOpen={toggleLoginOpen}
            />
        </>
    );
};

export default DropPage;
